import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { JsonTableEditor } from '../../../ui/form/fields';
import '../ui/adminUi.css';
import {
    datosContablesApi,
    seguimientoPlataformaApi,
    seguimientoCoffeApi,
    certificacionesObraApi,
    proyectosApi
} from '../../../api/api';
import { useBloqueoProyecto } from '../../../hooks/useBloqueoProyecto';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useNotifications } from '../../../ui/notifications';
import { ProyectoDTO } from '../../../api/Objetos';
import { slugify } from '../../shared/utils/slugify';
import { ensureArray } from '../../shared/utils/api';
import {createDefaultAccountingTable} from '../projects/accountingTable';

const JSON_EDITORS_CONFIG = {
    'datos-contables-variables': {
        path: 'datosVariables',
        fetch: (id: number) => datosContablesApi.get(id),
        save: (id: number, data: any) => datosContablesApi.save(id, data)
    },
    'seguimiento-plataforma': {
        path: 'datosVariables',
        fetch: (id: number) => seguimientoPlataformaApi.get(id),
        save: (id: number, data: any) => seguimientoPlataformaApi.save(id, data)
    },
    'seguimiento-coffe': {
        path: 'datosVariables',
        fetch: (id: number) => seguimientoCoffeApi.get(id),
        save: (id: number, data: any) => seguimientoCoffeApi.save(id, data)
    },
    'certificaciones-obra': {
        path: 'datosVariables',
        fetch: (id: number) => certificacionesObraApi.get(id),
        save: (id: number, data: any) => certificacionesObraApi.save(id, data)
    },
} as const;

export function AdminJsonEditorPage() {
    const { projectSlug, jsonSlug } = useParams<{ projectSlug: string, jsonSlug: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { data: projects, isLoading, isFetching } = useQuery<ProyectoDTO[]>({
        queryKey: ['admin', 'proyectos'],
        queryFn: () => proyectosApi.list(),
        select: (items: unknown) => ensureArray<ProyectoDTO>(items).map(p => ({...p, slug: slugify(p.informacionGeneral?.denominacion ?? '')})),
    });

    const isResolvingId = isLoading || isFetching;

    if (isResolvingId) {
        return (
            <div className="adminPageWrap">
                <div className="projectLoading">
                    <p>{t('adminJsonEditor.loading')}</p>
                </div>
            </div>
        );
    }

    const proyecto = projects?.find((p: ProyectoDTO) => p.slug === projectSlug);

    if (!proyecto || !proyecto.id) {
        window.alert(t('adminProjectEdit.projectNotFound'));
        navigate('/administracion/proyectos');
        return null;
    }

    return <JsonEditor projectId={proyecto.id} projectSlug={projectSlug!} jsonSlug={jsonSlug!} />;
}

function JsonEditor(props: { projectId: number, projectSlug: string, jsonSlug: string }) {
    const { projectId, projectSlug, jsonSlug } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { usuario, isAdmin } = useAuth();
    const { showSuccess, showError } = useNotifications();
    const userId = Number(usuario?.id || 0);

    const {
        permitido: tienePermisoEdicion,
        cargando: cargandoBloqueo,
        nombreUsuario: nombreUsuarioBloqueo
    } = useBloqueoProyecto(projectId, isAdmin ? userId : undefined);

    const configKey = Object.keys(JSON_EDITORS_CONFIG).find(key => jsonSlug?.startsWith(key)) as keyof typeof JSON_EDITORS_CONFIG;
    const config = configKey ? JSON_EDITORS_CONFIG[configKey] : null;

    const [initialValue, setInitialValue] = React.useState<any>(null);
    const [localValue, setLocalValue] = React.useState<any>(null);
    const [isLoaded, setIsLoaded] = React.useState(false);

    const sectionQ = useQuery({
        queryKey: ['section-data', jsonSlug, projectId],
        queryFn: () => config?.fetch(projectId!),
        enabled: !!projectId && !!config,
        staleTime: 0
    });

    React.useEffect(() => {
        if (sectionQ.isSuccess && !isLoaded) {
            const data = sectionQ.data;
            let extractedValue = data && config && (config.path in data) ? (data as any)[config.path] : null;

            if (typeof extractedValue === 'string') {
                try {
                    extractedValue = JSON.parse(extractedValue);
                    if (typeof extractedValue === 'string') extractedValue = JSON.parse(extractedValue);
                } catch(e) {}
            }

            if (!extractedValue || typeof extractedValue !== 'object' || !Array.isArray(extractedValue.rows)) {
                extractedValue = configKey === 'datos-contables-variables'
                    ? createDefaultAccountingTable()
                    : { columns: ["1"], rows: [[""]], cellStyles: {} };
            }

            setInitialValue(extractedValue);
            setLocalValue(extractedValue);
            setIsLoaded(true);

        } else if (sectionQ.isError && !isLoaded) {
            const emptyValue = configKey === 'datos-contables-variables'
                ? createDefaultAccountingTable()
                : { columns: ["1"], rows: [[""]], cellStyles: {} };
            setInitialValue(emptyValue);
            setLocalValue(emptyValue);
            setIsLoaded(true);
        }
    }, [sectionQ.data, sectionQ.isSuccess, sectionQ.isError, config, configKey, isLoaded]);

    const hasChanges = React.useMemo(() => {
        if (!initialValue || !localValue) {
            return false;
        }
        return JSON.stringify(initialValue) !== JSON.stringify(localValue);
    }, [initialValue, localValue]);

    if (!config) return <Navigate to="/administracion/proyectos" replace />;

    const isLoading = sectionQ.isLoading || localValue === null || cargandoBloqueo;
    const estaBloqueado = !isAdmin || !tienePermisoEdicion;

    const handleSave = () => {
        if (estaBloqueado) return;
        const payload = { ...(sectionQ.data || {}), [config.path]: localValue };
        config.save(projectId, payload)
            .then(() => {
                queryClient.invalidateQueries({ queryKey: ['section-data', jsonSlug, projectId] });
                showSuccess(t('adminJsonEditor.saveSuccess'));
                setInitialValue(localValue);
            })
            .catch(err => {
                showError(t('adminJsonEditor.saveError', { error: err.message }));
            });
    };

    const pageActions = (
        <div className="adminPageActions">
            {!estaBloqueado && (
                <button
                    className="btn btnPrimary"
                    disabled={!hasChanges || sectionQ.isFetching || isLoading}
                    onClick={handleSave}
                >
                    {sectionQ.isFetching ? t('adminJsonEditor.saving') : t('adminJsonEditor.saveChanges')}
                </button>
            )}
            <button className="btn btnSecondary" onClick={() => navigate(`/administracion/proyectos/${projectSlug}`)}>
                {t('adminJsonEditor.back')}
            </button>
        </div>
    );

    if (isLoading) {
        return (
            <div className="adminPageWrap">
                <div className="projectLoading">
                    <p>{t('adminJsonEditor.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="adminPageWrap">
            <div className="adminToolbar">
                <div>
                    <div className="adminPageEyebrow">
                        {!isAdmin ? t('adminJsonEditor.consultMode') : estaBloqueado
                            ? t('adminJsonEditor.readOnlyMode', { user: nombreUsuarioBloqueo || t('adminJsonEditor.otherUser') })
                            : t('adminJsonEditor.activeEditing')}
                    </div>
                    <h1 className="adminContentCardTitle" style={{ margin: 0 }}>
                        {t(`adminJsonEditor.titles.${configKey}`)}
                    </h1>
                </div>
                {pageActions}
            </div>

            <div className="adminTableWrap">
                <JsonTableEditor
                    label=""
                    value={localValue}
                    fullHeight={true}
                    readOnly={estaBloqueado}
                    onChange={setLocalValue}
                />
            </div>
        </div>
    );
}
